// src/locales/en.js

export const en = {
    // Navigation
    nav: {
        title: 'Turf Manager',
        upload: 'Data Upload',
        dashboard: 'General Dashboard',
        data: 'TurfBox Data',
        units: 'Units',
        irrigation: 'Irrigation',
        heating: 'Heating',
        map: 'Sensor Map',
        records: 'Records',
        team: 'Team',
        field: 'Field',
        allTeams: 'All Teams',
        allFields: 'All Fields',
        uploadedFile: 'Uploaded File',
        profile: 'Admin',
        language: 'Language',
        english: 'English',
        turkish: 'Turkish',
        settings: 'Settings',
        reports: 'Reports',
        help: 'Help',
        logout: 'Logout',
        notifications: 'Notifications',
        unread: 'unread',
        viewAll: 'View All',
        reports: 'Reports',
        infoTeam: 'Team: {team}',
        infoField: 'Field: {field}',
        infoRecordCount: 'TurfBox Record Count: {count}',
        infoDeviceCount: 'TurfBox Device Count: {count}',
        infoUnitCount: 'Unit Count:',
        infoIrrigationSystems: 'Irrigation Systems:',
        infoHeatingSystems: 'Heating Systems:',
        noData: 'No Data'
    },

    // Reports
    reports: {
        title: 'Ready Reports',
        subtitle: 'Performance and status reports automatically generated by the system',
        monthlyPerformance: 'Monthly Performance Report',
        monthlyPerformanceDesc: 'Monthly water consumption, fertilization and maintenance performance analysis of all fields.',
        deviceStatus: 'Device Status Report',
        deviceStatusDesc: 'Active/Passive devices, battery status and calibration history.',
        usageStats: 'Usage Statistics',
        usageStatsDesc: 'User logins, data upload frequency and system activities.',
        preparing: 'Preparing report, please wait...'
    },

    // Upload Page
    upload: {
        title: 'Excel Data Upload',
        subtitle: 'Upload TurfBox sensor data to the system',
        clickToUpload: 'Click or drag to upload your Excel file',
        supportedFormats: 'Supported formats: .xlsx, .xls, .csv',
        supportedFormatsWithCount: 'Supported formats: .xlsx, .xls, .csv ({count} records)',
        fileSelected: 'Selected',
        processing: 'Processing data... Please wait.',
        success: 'records uploaded successfully!',
        successMessage: 'Data converted to TurfBox format. You can preview below.',
        error: 'Error uploading file. Please check the file format.',
        unknownTeam: 'Unknown Team',
        unknownField: 'Unknown Field',
        fileFormat: 'File Name Format',
        fileFormatExample: 'Example: Galatasaray-TurkTelekom-20251230-Training.xlsx',
        exportExcel: 'Export to Excel',
        totalRecords: 'Total Records',
        activeDevices: 'Active Devices',
        avgTemperature: 'Avg. Temperature',
        avgMoisture: 'Avg. Moisture',
        avgSoilMoisture: 'Avg. Soil Moisture',
        loadedFromExcel: 'Loaded from Excel',
        onlineSensors: 'Online sensors',
        airTempAverage: 'Average air temperature',
        moistureAverage: 'Average moisture',
        dataPreview: 'Data Preview',
        showing: 'showing',
        of: 'of',
        total: 'total',
        previewLimit: 'Showing first 50 records / Total {total} records',
        maxFileSize: 'Maximum file size: 50MB',
        noteLabel: 'ℹ️ Note:',
        performanceNote: 'Only the first 50 records are shown for performance.',
        exportAll: 'Export all data to Excel →',
        exportAllWithCount: 'Export all {total} records to Excel.',
        exportHeaders: {
            device: 'Device',
            location: 'Location',
            airTemp: 'Air Temp (°C)',
            humidity: 'Humidity (%)',
            co2: 'CO₂ (ppm)',
            wind: 'Wind (km/h)',
            lux: 'Lux',
            soilMoisture: 'Soil Moisture (%)',
            soilTemp: 'Soil Temp (°C)',
            ph: 'pH',
            ec: 'EC (mS)',
            status: 'Status'
        },
        exportSheetName: 'TurfBox Data',
        exportFilePrefix: 'TurfBox_Data',
        tableHeaders: {
            index: 'No',
            device: 'Device',
            location: 'Location',
            airTemp: 'Air Temp.',
            humidity: 'Humidity',
            co2: 'CO₂',
            wind: 'Wind',
            lux: 'Lux',
            soilMoisture: 'Soil Moist.',
            soilTemp: 'Soil Temp.',
            status: 'Status'
        },
        previewSummary: 'Data Preview (First 20 Records / Total {total})'
    },

    // Dashboard
    dashboard: {
        title: 'General Dashboard',
        subtitle: 'Summary and analysis of all sensor data',
        liveWeather: 'Live Weather',
        feelsLike: 'Feels like',
        humidity: 'Humidity',
        wind: 'Wind',
        visibility: 'Visibility',
        lastUpdate: 'Last update',
        loading: 'Loading...',
        noData: 'Could not retrieve data',
        location: '📍 Kocaeli, Türkiye',
        weatherSource: 'Source: Google Maps (Demo)',
        totalRecordsAria: 'Go to total records table',
        activeDevicesAria: 'Go to active devices table',
        zoneCountAria: 'Go to zone count map',
        totalAlertsAria: 'Go to notifications list',
        totalRecords: 'Total Records',
        totalTurfBoxRecords: 'Total TurfBox Record Count',
        allMeasurements: 'All sensor measurements',
        activeDevices: 'Active Devices',
        sensors: 'TurfBox sensors',
        turfBoxDeviceCount: 'Total TurfBox Device Count',
        turfBoxSummary: 'TurfBox Summary',
        turfBoxActive: '{count} Active',
        turfBoxInactiveTotal: '{inactive} Inactive, {total} Total',
        units: 'Units',
        unitDetail: 'Field units',
        unitsSummary: 'Units',
        unitsCount: '{count} units',
        irrigationSystems: 'Irrigation Systems',
        irrigationDetail: 'Irrigation system list',
        irrigationSummary: 'Irrigation',
        irrigationDuration: '{minutes} min',
        irrigationTodayTotal: 'Today total',
        heatingStatus: 'Heating',
        heatingStatusDetail: 'Heating system status',
        heatingSummary: 'Heating',
        online: 'Online',
        offline: 'Offline',
        viewDetails: 'View details',
        deviceLabel: 'Device',
        selectDevice: 'Select a device',
        zoneCount: 'Zone Count',
        locations: 'Different locations',
        totalAlerts: 'Total Alerts',
        needsAttention: 'Needs attention',
        environmental: 'Environmental Conditions',
        chartDateRange: 'Chart date range',
        chartDateRangeHint: 'Dates are interpreted as UTC.',
        startDate: 'Start',
        endDate: 'End',
        apply: 'Apply',
        clear: 'Clear',
        temperature: 'Temperature',
        airAvg: 'Air (Avg.)',
        soilAvg: 'Soil (Avg.)',
        range: 'Range',
        optimal: 'Optimal level',
        outOfRange: 'Out of ideal range',
        moisture: 'Moisture',
        illumination: 'Illumination',
        other: 'Other',
        alerts: 'Situations Requiring Attention',
        lowMoisture: 'Low Moisture',
        highTemp: 'High Temperature',
        lowPH: 'Low pH',
        highPH: 'High pH',
        lowMoistureDetail: 'Zone < 20% moisture',
        highTempDetail: 'Zone > 20°C',
        lowPHDetail: 'Zone < 6.0 pH',
        highPHDetail: 'Zone > 7.5 pH',
        weatherPartlyCloudy: 'Partly cloudy',
        deviceDistribution: 'Moisture Distribution by Device',
        zoneDistribution: 'Temperature Distribution by Zone'
    },

    // Lists
    lists: {
        totalFiles: 'Total files: {count}',
        empty: {
            title: 'No data found yet',
            description: 'Add files to the manifest to show a summary table here.'
        },
        table: {
            index: '#',
            file: 'File',
            folder: 'Folder'
        },
        heating: {
            title: 'Heating List',
            subtitle: 'Summary of heating system files',
            filters: {
                title: 'Filters',
                subtitle: 'Filter heating records',
                system: 'System',
                date: 'Date Range',
                startDate: 'Start',
                endDate: 'End',
                status: 'Status',
                apply: 'Apply',
                all: 'All'
            },
            table: {
                timestamp: 'Date/Time',
                system: 'System',
                inletTemp: 'Inlet Temp',
                outletTemp: 'Outlet Temp',
                deltaT: 'Delta T',
                pressure: 'Pressure',
                status: 'Status'
            }
        },
        irrigation: {
            title: 'Irrigation List',
            subtitle: 'Summary of irrigation system files',
            tabs: {
                valves: 'Valve List',
                soil: 'Soil Data'
            },
            table: {
                valves: {
                    valve: 'Valve',
                    location: 'Location',
                    status: 'Status',
                    actions: 'Actions'
                },
                soil: {
                    location: 'Location',
                    soilTemperature: 'Soil Temperature',
                    soilMoisture: 'Soil Moisture',
                    lastIrrigation: 'Last Irrigation',
                    status: 'Status'
                }
            },
            actions: {
                soilData: 'Soil Data'
            },
            empty: {
                title: 'No soil data available',
                description: 'Upload soil data files to populate this tab.'
            }
        },
        units: {
            title: 'Unit List',
            subtitle: 'Summary of unit files'
        }
    },

    // Notifications
    notifications: {
        subtitle: 'Latest notifications from the system',
        lowMoistureDetected: 'Low moisture detected in {count} zones',
        newDataLoaded: 'New data loaded: {count} records',
        systemUpdateComplete: 'System update completed',
        minutesAgo: '{count} min ago',
        hoursAgo: '{count} hour ago',
        types: {
            warning: 'Warning',
            info: 'Info',
            success: 'Success'
        }
    },

    // Data Loader
    dataLoader: {
        loadIssueTitle: 'Data Upload Issue',
        fileLocationLabel: '📁 File Location:',
        step1Prefix: 'Add your Excel files to the',
        step1Suffix: 'folder',
        step2Label: 'File names:',
        step3Suffix: 'update the file list inside',
        sampleFileNames: '📝 Sample File Names:',
        retry: 'Try Again',
        loadingTitle: 'Loading Data',
        loadingMessage: 'Reading Excel files, please wait...',
        consoleHintPrefix: 'For detailed information, check',
        consoleHintSuffix: ' (F12)',
        loadErrorEmpty: 'Data could not be loaded or files are empty',
        loadError: 'An error occurred while loading Excel files'
    },

    // TurfBox Table
    table: {
        title: 'TurfBox Data',
        totalGroups: 'Total Groups',
        deviceLocation: 'Device × Location',
        totalRecords: 'Total Records',
        filtered: 'Filtered',
        avgMoisture: 'Avg. Moisture',
        soilMoisture: 'Soil moisture',
        avgTemp: 'Avg. Temperature',
        avgPH: 'Avg. pH',
        soilTemp: 'Soil temperature',
        dateRange: 'Date Range',
        all: 'All',
        noFilter: 'No filter',
        filteredData: 'Filtered data',
        dateFilter: 'Date Filter',
        clearFilter: 'Clear Filter',
        startDate: 'Start Date',
        endDate: 'End Date',
        quickFilters: 'Quick Filters',
        last24Hours: 'Last 24 Hours',
        last7Days: 'Last 7 Days',
        last30Days: 'Last 30 Days',
        allData: 'All Data',
        activeFilter: 'Active Filter',
        availableRange: 'Available data range',
        searchPlaceholder: 'Search device or location...',
        allDevices: 'All Devices',
        allLocations: 'All Locations',
        device: 'Device',
        location: 'Location',
        records: 'Records',
        min: 'Min',
        max: 'Max',
        status: 'Status',
        detail: 'Detail',
        hide: 'Hide',
        optimal: 'Optimal',
        good: 'Good',
        attention: 'Attention',
        summary: 'Summary Information',
        totalMeasurements: 'Total Measurements',
        airTemp: 'Air Temperature',
        airHumidity: 'Air Humidity',
        detailedRecords: 'Detailed Records',
        last20Records: 'Last',
        showingFirst: 'of',
        showing: 'showing',
        noRecords: 'No records found',
        changeFilters: 'Try changing filters',
        howItWorks: 'How It Works?',
        infoText1: 'Each row represents a Device × Location combination',
        infoText2: 'Displayed values are averages for that group',
        infoText3: 'Click "Detail" button to see all individual records',
        infoText4: 'Status colors: 🟢 Optimal, 🟡 Good, 🔴 Attention (based on moisture and temperature)',
        exportExcel: 'Export to Excel',
        dateFilterHint: 'You can filter by date range.',
        noStart: 'No start date',
        noEnd: 'No end date',
        filterSummary: '{filtered} records / {total} total',
        sensorMap: 'Sensor Map',
        summaryWithContext: 'Summary Information - {device} ({location})',
        detailedRecordsWithCount: 'Detailed Records (Last {count} records)',
        detailHeaders: {
            dateTime: 'Date/Time',
            airTemp: 'Air Temp.',
            airHumidity: 'Air Humidity',
            wind: 'Wind',
            lux: 'Lux',
            soilMoisture: 'Soil Moist.',
            soilTemp: 'Soil Temp.'
        },
        showingFirstRecords: 'Showing the first 20 of {total} records',
        statuses: {
            normal: 'Normal',
            critical: 'Critical'
        },
        export: {
            device: 'Device',
            location: 'Location',
            date: 'Date',
            airTemp: 'Air Temp (°C)',
            humidity: 'Humidity (%)',
            recordCount: 'Record Count',
            avgAirTemp: 'Air Temp (°C)',
            avgHumidity: 'Humidity (%)',
            co2: 'CO₂ (ppm)',
            wind: 'Wind (km/h)',
            lux: 'Lux',
            soilMoisture: 'Soil Moisture (%)',
            avgSoilMoisture: 'Soil Moisture (%)',
            minMoisture: 'Min Moisture (%)',
            maxMoisture: 'Max Moisture (%)',
            avgSoilTemp: 'Soil Temp (°C)',
            minTemp: 'Min Temp (°C)',
            maxTemp: 'Max Temp (°C)',
            ph: 'pH',
            ec: 'EC (mS)',
            status: 'Status',
            lastUpdate: 'Last Update',
            sheetName: 'TurfBox Summary',
            fileNamePrefix: 'TurfBox_Summary'
        },
        paginationSummary: '{start} - {end} shown (Total {total} records)',
        pageLabel: 'Page {current} / {total}'
    },

    // Sensor Map
    map: {
        title: 'Sensor Map - 30 Zones',
        activeZones: 'Active Zones',
        avgMoisture: 'Avg. Moisture',
        avgTemp: 'Avg. Temperature',
        avgPH: 'Avg. pH',
        lowMoisture: 'Low Moisture',
        highTemp: 'High Temp.',
        soilMoisture: 'Soil Moisture',
        temperature: 'Temperature',
        phLevel: 'pH Level',
        fieldView: 'Field View',
        clickForDetails: 'Click zone to view details',
        colorLegend: 'Color Legend',
        dry: 'Dry',
        ideal: 'Ideal',
        wet: 'Wet',
        acidic: 'Acidic',
        basic: 'Basic',
        zoneDetails: 'Zone',
        details: 'Details',
        close: 'Close',
        lastUpdate: 'Last Update',
        criticalLegend: 'Critical (moisture < 20% / temperature > 20°C)',
        normal: 'Normal',
        zoneDetailsTitle: 'Zone {zone} - Details',
        soilTemp: 'Soil Temperature',
        airTemp: 'Air Temperature',
        airHumidity: 'Air Humidity',
        unitLabel: 'Unit',
        turfBoxLabel: 'TurfBox',
        heatingLabel: 'Heating',
        unitInfo: 'Basic status and field placement details for the unit system.',
        turfboxInfo: 'Summary information and general placement for the TurfBox sensor station.',
        heatingInfo: 'Core operation and placement details for the heating system.',
        statuses: {
            normal: 'Normal',
            critical: 'Critical',
            noData: 'No Data'
        }
    },

    // Common
    common: {
        search: 'Search',
        filter: 'Filter',
        export: 'Export',
        import: 'Import',
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        add: 'Add',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        warning: 'Warning',
        info: 'Info',
        yes: 'Yes',
        no: 'No',
        ok: 'OK',
        date: 'Date',
        time: 'Time',
        name: 'Name',
        description: 'Description',
        actions: 'Actions'
    },

    // Units
    units: {
        totalUnits: 'Total Units: {count}',
        celsius: '°C',
        percent: '%',
        ppm: 'ppm',
        kmh: 'km/h',
        ms: 'm/s',
        miliSiemens: 'mS',
        km: 'km',
        lux: 'lux',
        filters: {
            title: 'Filters',
            subtitle: 'Narrow by unit and date range',
            unit: 'Unit',
            startDate: 'Start Date',
            endDate: 'End Date',
            apply: 'Apply Filters',
            all: 'All'
        },
        table: {
            dateTime: 'Date/Time',
            unit: 'Unit',
            unitFallback: 'Unit',
            location: 'Location',
            tempHumidity: 'Temp/Humidity',
            temperature: 'Temperature',
            humidity: 'Humidity',
            status: 'Status',
            actions: 'Actions',
            noData: 'No Data'
        },
        status: {
            normal: 'Normal',
            attention: 'Attention'
        },
        actions: {
            detail: 'Detail',
            sensorMap: 'Sensor Map'
        },
        graphView: {
            button: 'Graph View',
            title: 'Graph View',
            subtitle: 'Summary tiles for the TurfBox data list.',
            lastUpdate: 'Last update: {value}',
            cards: {
                totalUnits: 'Total Units',
                totalUnitsHint: 'Filtered results',
                attention: 'Needs Attention',
                attentionHint: 'Temperature & humidity alerts',
                avgTemperature: 'Avg Temperature',
                avgTemperatureHint: 'Last 24h readings',
                avgHumidity: 'Avg Humidity',
                avgHumidityHint: 'Sensor average'
            }
        },
        detail: {
            subtitle: 'Unit Detail',
            summary: 'Summary',
            fileInfo: 'File Info',
            fileName: 'File Name',
            locationLabel: 'Location',
            back: 'Back to List',
            emptyTitle: 'Unit not found',
            emptyDescription: 'Select a unit from the list to see details.'
        }
    }
};
